// Task Constants - Used by external dependencies (Dashboard, Templates, Boards)

export const DEFAULT_GROUP_BY = "due_date";

export const GROUP_OBJ: Record<string, string> = {
  "0": "None",
  "1": "Flows",
  "2": "Internal Clients",
  "3": "External Clients",
};

export const PRIORITY_OBJ: Record<string, string> = {
  "1": "Low",
  "2": "Medium",
  "3": "High",
};

export const STATUS_OBJ: Record<string, string> = {
  "1": "To Do",
  "2": "In Progress",
  "3": "Completed",
  "4": "In Review",
  "5": "Overdue",
};

export const GROUPINGS_MAP: Record<string, string> = {
  due_date: "due_date",
  priority: "priority",
  status: "status",
  assignee: "assignee",
};

export const IMPORT_FIELDS = [
  "title",
  "description",
  "priority",
  "status",
  "due_date",
  "assignedTo",
  "tags",
];

export const USER_FIELDS = [
  "assignedTo",
  "approvedBy",
];

interface Preference {
  task?: {
    groupBy?: string;
    sortBy?: string;
    sortField?: string;
    columnSettings?: unknown[];
    tmpFilter?: {
      hide_completed_task?: boolean;
    };
  };
}

interface TaskFilter {
  flows?: number[];
  clients?: number[];
  hide_completed_task?: boolean;
  search?: string;
  [key: string]: unknown;
}

interface AssignedUser {
  id: number;
  first_name?: string;
  last_name?: string;
}

interface ApprovedBy {
  id?: number;
}

export const currentGroupBy = (preference: Preference | null): string => {
  return preference?.task?.groupBy || DEFAULT_GROUP_BY;
};

export const isDefaultGroupBy = (groupBy: string): boolean => {
  return groupBy === DEFAULT_GROUP_BY || !groupBy;
};

export const isDashboardPage = (type: string): boolean => {
  return type === "dashboard";
};

export const isTaskPage = (type: string): boolean => {
  return type === "task" || type === "Tasks" || !type;
};

export const isClientPage = (type: string): boolean => {
  return type === "Clients";
};

export const isFlowPage = (type: string): boolean => {
  return type === "Flows";
};

export const getPageFilters = (
  filter: TaskFilter,
  type: string,
  projectID: number | string | null,
  clientID: number | string | null,
  _userId: number
): TaskFilter => {
  const tmpFilter = { ...filter };

  if (projectID) {
    tmpFilter.flows = [Number(projectID)];
  }
  if (clientID) {
    tmpFilter.clients = [Number(clientID)];
  }

  return tmpFilter;
};

export const handleTaskRouteClose = (
  type: string,
  projectID: number | string | null,
  clientID: number | string | null
): void => {
  let path = "/tasks";
  if (type === "Flows" && projectID) {
    path = `/flows/${projectID}/tasks`;
  } else if (type === "Clients" && clientID) {
    path = `/clients/${clientID}/tasks`;
  }
  window.history.pushState({}, "", path);
};

export const hasUserAccess = (
  userId: number,
  agOwner: boolean,
  roleType: string,
  assignedTo: AssignedUser[] = [],
  approvedBy: ApprovedBy = {},
  createdBy: number | null = null
): boolean => {
  if (agOwner) return true;
  if (roleType === "admin") return true;
  if (createdBy === userId) return true;
  if (approvedBy?.id === userId) return true;
  if (assignedTo.some((u) => u.id === userId)) return true;
  return false;
};

export const getApprovalStatus = (
  userId: number,
  _assignedTo: AssignedUser[],
  approvedBy: ApprovedBy,
  _createdBy: number | null,
  agOwner: boolean
): number => {
  if (approvedBy?.id) {
    if (approvedBy.id === userId || agOwner) {
      return 3; // Completed
    }
    return 4; // In Review
  }
  return 3; // Completed
};

export const getSorting = (
  preference: Preference | null,
  defaults: { sortBy?: string; sortField?: string }
): { sortBy: string; sortField: string } => {
  return {
    sortBy: preference?.task?.sortBy || defaults?.sortBy || "desc",
    sortField: preference?.task?.sortField || defaults?.sortField || "created_at",
  };
};

interface TaskData {
  id: number;
  title: string;
  description?: string;
  priority?: string;
  status?: string;
  due_date?: string;
  assigned_to?: AssignedUser[];
  all_tags?: unknown[];
}

interface TaskUpdate {
  group_type?: string;
  priority?: string;
  status?: string;
  [key: string]: unknown;
}

interface CurrentTask {
  group_id?: unknown;
  [key: string]: unknown;
}

export const getNewVal = (
  newValue: TaskUpdate,
  currentTask: CurrentTask
): Record<string, unknown> => {
  const result: Record<string, unknown> = { ...newValue };

  // Transform group_type to display value
  if (newValue.group_type !== undefined) {
    const currentGrpId = currentTask?.group_id;
    result.group_type = currentGrpId && newValue.group_type
      ? GROUP_OBJ[newValue.group_type] || "-"
      : "-";
  }

  // Transform priority to display value
  if (newValue.priority !== undefined) {
    result.priority = newValue.priority
      ? PRIORITY_OBJ[newValue.priority] || "-"
      : "-";
  }

  // Transform status to display value
  if (newValue.status !== undefined) {
    result.status = newValue.status
      ? STATUS_OBJ[newValue.status] || "-"
      : "-";
  }

  return result;
};

